import React, { useEffect, useRef, useState } from "react";
import { Slider, Typography, useTheme } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import VolumeUp from '@mui/icons-material/VolumeUp';
import OpacityIcon from '@mui/icons-material/Opacity';
import LocalMoviesIcon from '@mui/icons-material/LocalMovies';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Fullscreen } from '@mui/icons-material';
import LayersIcon from '@mui/icons-material/Layers';
import { useSelector } from "react-redux";

import { useVideoController } from "./useVideoController";
import { getSelectedType } from "../../../commonFunction";
import './ElementContextMenu.scss';

function ElementContextMenu({ menuVisible, menuPosition }) {
  const ProjectData = useSelector((store) => store.projectdata.projectJSON);
  const selectedID = useSelector((store) => store.webgldata.selectedItemID);
  const [adjustedPosition, setAdjustedPosition] = useState({ x: -500, y: -500 });
  const [finalMenuVisible, setFinalMenuVisible] = useState(false);
  const menuRef = useRef(null);
  const theme = useTheme();

  const {
    ItemRemoveFunc,
    duplicateFunction,
    splitFunction,
    levelUpDown,
    selected,
    opacity,
    setOpacity,
    changedOpacity,
    durationValue,
    setDurationValue,
    changedDuration,
    stretchScreenVideo,
    volumeValue,
    setVolumeValue,
    changedVolume,
    onFreezeFrame,
    removeGroup,
    VideoPlayFunc
  } = useVideoController();

  const selectedIDs = Array.isArray(selectedID) ? selectedID.filter(Boolean) : selectedID ? [selectedID] : [];
  const selectedTypes = selectedIDs.map((id) => getSelectedType(id, ProjectData)).filter(Boolean);
  const primaryType = selectedTypes[0];
  const isSingle = selectedIDs.length === 1;
  const supportsOpacity = selectedIDs.some((id) => {
    const type = getSelectedType(id, ProjectData);
    return ProjectData[type]?.[id]?.alpha !== undefined;
  });
  const supportsVolume = isSingle && (primaryType === 'videos' || primaryType === 'audios');
  const supportsLayers = isSingle && ['videos', 'images', 'text'].includes(primaryType);

  useEffect(() => {
    if (menuVisible && menuRef.current) {
      const menuWidth = menuRef.current.offsetWidth || 270;
      const menuHeight = menuRef.current.offsetHeight;
      const x = Math.max(8, Math.min(menuPosition.x, window.innerWidth - menuWidth - 8));
      const y = Math.max(8, Math.min(menuPosition.y, window.innerHeight - menuHeight - 8));
      setAdjustedPosition({ x, y });
      setFinalMenuVisible(true);
      VideoPlayFunc(false);
    } else {
      setFinalMenuVisible(false);
    }
  }, [menuVisible, menuPosition]);

  const submenuSide = adjustedPosition.x + 450 > window.innerWidth ? 'context-submenu-left' : '';
  const commitDuration = () => changedDuration(durationValue);

  return (
    <div className={`context-menu-layer ${finalMenuVisible ? 'visible' : ''}`}>
      <div
        ref={menuRef}
        className="context-menu"
        style={{ top: adjustedPosition.y, left: adjustedPosition.x, backgroundColor: theme.palette.background.paper }}
      >
        <div className="context-menu-header">
          <strong>{selectedIDs.length > 1 ? `${selectedIDs.length} clips selected` : 'Clip actions'}</strong>
          <span>{isSingle ? primaryType : 'Bulk editing'}</span>
        </div>

        <div className="context-menu-section">
          <Typography component="div" className="context-menu-label">Edit</Typography>
          {isSingle && <button type="button" onClick={duplicateFunction}><ContentCopyIcon />Duplicate <kbd>Ctrl D</kbd></button>}
          {isSingle && <button type="button" onClick={splitFunction}><ContentCutIcon />Split <kbd>S</kbd></button>}
          <button type="button" onClick={ItemRemoveFunc}><DeleteOutlineIcon />Delete <kbd>Del</kbd></button>
          {isSingle && <button type="button" onClick={removeGroup}><DeleteSweepIcon />Delete track</button>}
        </div>

        <div className="context-menu-divider" />
        <div className="context-menu-section">
          <Typography component="div" className="context-menu-label">Adjust</Typography>
          {supportsOpacity && (
            <div className="context-menu-item has-submenu disableExit">
              <OpacityIcon />Opacity <span className="context-menu-value">{Math.round(opacity * 100)}%</span>
              <div className={`context-submenu disableExit ${submenuSide}`} style={{ backgroundColor: theme.palette.background.paper }}>
                <Typography className="disableExit">Opacity</Typography>
                <Slider value={opacity} valueLabelDisplay="auto" step={0.05} min={0} max={1} className="disableExit"
                  onChange={(event, value) => setOpacity(value)}
                  onChangeCommitted={(event, value) => changedOpacity(value)} />
              </div>
            </div>
          )}
          <div className="context-menu-item has-submenu disableExit">
            <AccessTimeIcon />Duration <span className="context-menu-value">{Number(durationValue).toFixed(1)}s</span>
            <div className={`context-submenu disableExit ${submenuSide}`} style={{ backgroundColor: theme.palette.background.paper }}>
              <label className="duration-control disableExit">
                <span>Duration in seconds</span>
                <input className="disableExit" type="number" min="0.1" step="0.1" value={durationValue}
                  onChange={(event) => setDurationValue(event.target.value)}
                  onBlur={commitDuration}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === 'Enter') commitDuration();
                  }} />
              </label>
            </div>
          </div>
          {supportsVolume && (
            <div className="context-menu-item has-submenu disableExit">
              <VolumeUp />Volume <span className="context-menu-value">{Math.round(volumeValue * 100)}%</span>
              <div className={`context-submenu disableExit ${submenuSide}`} style={{ backgroundColor: theme.palette.background.paper }}>
                <Typography className="disableExit">Volume</Typography>
                <Slider value={volumeValue} valueLabelDisplay="auto" step={0.1} min={0} max={1} className="disableExit"
                  onChange={(event, value) => setVolumeValue(value)} onChangeCommitted={changedVolume} />
              </div>
            </div>
          )}
        </div>

        {supportsLayers && (
          <>
            <div className="context-menu-divider" />
            <div className="context-menu-section">
              <Typography component="div" className="context-menu-label">Arrange</Typography>
              <div className="context-menu-item has-submenu disableExit">
                <LayersIcon />Layer <span className="context-menu-value">{Number(selected?.coordinate?.zIndex || 0) + 9}</span>
                <div className={`context-submenu disableExit ${submenuSide}`} style={{ backgroundColor: theme.palette.background.paper }}>
                  <button type="button" className="disableExit" onClick={() => levelUpDown(1)}><ArrowUpwardOutlinedIcon />Bring forward</button>
                  <button type="button" className="disableExit" onClick={() => levelUpDown(-1)}><ArrowDownwardOutlinedIcon />Send backward</button>
                </div>
              </div>
              <button type="button" onClick={stretchScreenVideo}><Fullscreen />Fit canvas</button>
              {primaryType === 'videos' && <button type="button" onClick={onFreezeFrame}><LocalMoviesIcon />Freeze frame</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ElementContextMenu;
